import {
  CheckCheck,
  CheckIcon,
  CircleAlert,
  CircleAlertIcon,
  Eye,
  EyeClosed,
  HandPlatter,
  Handshake,
  MapPin,
  MessageCircle,
  Paperclip,
  Pen,
  Phone,
  Send,
  Signature,
  Store,
  TimerIcon,
  User,
  UserCog,
  Button,
} from "../../../ui";
import { Checkbox } from "../../../ui/Checkbox";
import { useContext, useId } from "react";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";

const EVENT_TYPE = {
  ticket_created: <Pen size={13} />,
  assigned: <Handshake size={13} />,
  reassigned: <Handshake size={13} />,
  status_changed: <CheckIcon size={13} />,
  priority_changed: <CircleAlert size={13} />,
  message_added: <MessageCircle size={13} />,
  attachment_uploaded: <Paperclip size={13} />,
  sla_breached: <CheckCheck size={13} />,
  resolved: <CheckCheck size={13} />,
  closed: <EyeClosed size={13} />,
  reopened: <Eye size={13} />,
};

const PRIORITIES = ["low", "normal", "high", "urgent", "critical"];

// "status_changed" -> "status changed" for display.
const humanize = (value) => String(value || "-").replace(/_/g, " ");

export default function TicketDetails({
  selectedTicket,
  detailsLoading,
  customFields,
  t,
  reply,
  setReply,
  isInternalNote,
  setIsInternalNote,
  pendingAction,
  actionError,
  handleReply,
  handleClose,
  handleSetResolved,
  handleSetInProgress,
  handlePriority,
}) {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const replyId = useId();
  if (!selectedTicket) return null;

  const isStoreSide = can("support.ticket.clientSide");
  const busy = Boolean(pendingAction);
  const locale = lang === "ar" ? "ar-SA" : "en-US";
  const messages = (selectedTicket?.messages || []).filter(
    // Internal notes are for staff only.
    (message) => !(message?.internal && isStoreSide),
  );

  return (
    <section
      className="support-ticket-details"
      aria-labelledby={`${replyId}-title`}
      aria-busy={detailsLoading || busy || undefined}
    >
      <div className="details-header">
        <div>
          <div className="details-id">
            {selectedTicket?.ticketNumber || selectedTicket?._id}
          </div>

          <h3 id={`${replyId}-title`}>{selectedTicket?.title}</h3>
          <div className="ticket-tags">
            <div className="ticket-tag">
              <Store size={12} aria-hidden="true" />
              {selectedTicket?.storeId?.id}{" "}
              {selectedTicket?.storeId?.businessName}
            </div>
            <div className="ticket-tag">
              <HandPlatter size={12} aria-hidden="true" />
              {selectedTicket?.branchId?.name}
            </div>
            <div className="ticket-tag">
              <MapPin size={12} aria-hidden="true" />
              {selectedTicket?.branchId?.city}
            </div>
          </div>
          {!isStoreSide && (
            <div className="ticket-tags">
              <div className="ticket-tag">
                <CircleAlertIcon size={12} aria-hidden="true" />
                {t.priority}: {t[selectedTicket?.priority] || selectedTicket?.priority}
              </div>
              <div className="ticket-tag">
                <User size={12} aria-hidden="true" />
                {selectedTicket?.createdBy?.name}
              </div>
              <div className="ticket-tag">
                <Phone size={12} aria-hidden="true" />
                <span dir="ltr">{selectedTicket?.createdBy?.phone}</span>
              </div>
              <div className="ticket-tag">
                <Signature size={12} aria-hidden="true" />
                {selectedTicket?.createdBy?.approved ? t.approved : t.disapproved}
              </div>
              <div className="ticket-tag">
                <UserCog size={12} aria-hidden="true" />
                {selectedTicket?.assignedTo?.name}
              </div>
              <div className="ticket-tag">
                <TimerIcon size={12} aria-hidden="true" />
                {selectedTicket?.createdAt
                  ? new Date(selectedTicket.createdAt).toLocaleDateString(locale)
                  : "-"}
              </div>
            </div>
          )}
        </div>

        <span className={`ticket-badge ${selectedTicket?.status}`}>
          {t[selectedTicket?.status] || selectedTicket?.status}
        </span>
      </div>

      {detailsLoading ? (
        <div className="support-ticket-empty" role="status">
          {t.loading}
        </div>
      ) : (
        <>
          <div className="details-body">
            {customFields.map((field) => (
              <DetailRow
                key={field.key || field.label}
                label={field.label}
                value={field.value}
              />
            ))}
          </div>

          {!isStoreSide && (
            <div className="ticket-timeline">
              <h4 className="ticket-section-title">{t.timeline}</h4>

              {(selectedTicket?.events || []).map((event, index) => (
                <div className="ticket-timeline-item" key={index}>
                  <div className="timeline-dot" aria-hidden="true">
                    {EVENT_TYPE[event.type]}
                  </div>

                  <div>
                    <div className="timeline-action">
                      {humanize(event?.type || event?.action)}
                    </div>

                    <div className="timeline-date">
                      {event?.createdAt
                        ? new Date(event.createdAt).toLocaleString(locale)
                        : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="ticket-timeline">
            <h4 className="ticket-section-title">{t.messages}</h4>

            {messages.length === 0 && (
              <p className="timeline-date">{t.noMessages}</p>
            )}
            {messages.map((message, index) => (
              <div className="ticket-timeline-item" key={message?._id || index}>
                <div
                  className={`timeline-dot ${message?.internal ? "internal" : ""}`}
                  aria-hidden="true"
                >
                  <MessageCircle size={13} />
                </div>

                <div className="ticket-message">
                  <div className="timeline-date">
                    {message?.sender?.name || "-"}
                    {message?.internal && (
                      <span className="ticket-internal-tag">{t.internal}</span>
                    )}
                  </div>
                  <div className="timeline-action">{message?.message || "-"}</div>

                  <div className="timeline-date">
                    {message?.createdAt
                      ? new Date(message.createdAt).toLocaleString(locale)
                      : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="reply-box">
            {actionError && (
              <p className="support-action-error" role="alert">
                {actionError}
              </p>
            )}
            <label htmlFor={replyId} className="support-visually-hidden">
              {t.replyLabel}
            </label>
            <textarea
              id={replyId}
              value={reply}
              placeholder={t.replyPlaceholder}
              onChange={(e) => setReply(e.target.value)}
              disabled={pendingAction === "reply"}
            />
            {!isStoreSide && (
              <Checkbox
                label={t.isInternal}
                checked={isInternalNote}
                disabled={busy}
                onChange={() => {
                  setIsInternalNote((prev) => !prev);
                }}
              />
            )}

            <div className="reply-actions">
              {!isStoreSide && (
                <>
                  <Button
                    type="button"
                    variant="neutral"
                    appearance="soft"
                    className="btn-light"
                    disabled={busy}
                    loading={pendingAction === "close"}
                    onClick={handleClose}
                  >
                    {t.close}
                  </Button>

                  <Button
                    type="button"
                    variant="primary"
                    appearance="filled"
                    className="btn-purple"
                    disabled={busy}
                    loading={pendingAction === "resolved"}
                    onClick={handleSetResolved}
                  >
                    {t.resolved}
                  </Button>

                  <Button
                    type="button"
                    variant="primary"
                    appearance="filled"
                    className="btn-purple"
                    disabled={busy}
                    loading={pendingAction === "in_progress"}
                    onClick={handleSetInProgress}
                  >
                    {t.setInProgress}
                  </Button>
                </>
              )}

              <Button
                type="button"
                variant="primary"
                appearance="filled"
                className="btn-dark"
                icon={<Send size={14} />}
                disabled={busy || !reply.trim()}
                loading={pendingAction === "reply"}
                onClick={handleReply}
              >
                {t.reply}
              </Button>
            </div>

            {!isStoreSide && (
              <div className="reply-actions" role="group" aria-label={t.setPriority}>
                {PRIORITIES.map((priority) => (
                  <Button
                    key={priority}
                    type="button"
                    variant="neutral"
                    appearance="soft"
                    className={`btn-light ${
                      selectedTicket?.priority === priority ? "is-current" : ""
                    }`}
                    aria-pressed={selectedTicket?.priority === priority}
                    disabled={busy}
                    loading={pendingAction === `priority:${priority}`}
                    onClick={() => handlePriority(priority)}
                  >
                    {t[priority]}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

const DetailRow = ({ label, value }) => (
  <div className="detail-row">
    <span>{label}</span>
    {/* Checkbox fields hold booleans, which React renders as nothing. */}
    <strong>
      {typeof value === "boolean" ? (value ? "✓" : "✗") : String(value ?? "-")}
    </strong>
  </div>
);
